import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import { Button, Collapse, IconButton, SvgIcon, Tooltip } from '@mui/material';

import IconClose from 'app/ui-kit/icons/Close.svg';
import IconSettings from 'app/ui-kit/icons/Settings.svg';
import IconZoom from 'app/ui-kit/icons/Zoom.svg';
import useShortcutSearchInput from 'hooks/use-shortcut-search-input';
import useStores from 'hooks/use-stores';
import SearchField from 'main/components/search/SearchField';
import SearchBarSettings from 'main/components/search/Settings';

const useStyles = makeStyles()({
    searchBarRoot: {
        marginBottom: 24
    },
    searchBar: {
        display: 'flex'
    },
    searchField: {
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        background: '#F5F5F5',
        borderRadius: 18
    },
    searchFieldInput: {
        color: '#BDBDBD',
        padding: '10px 0 10px 24px',
        '& .MuiInput-input': {
            fontSize: 14
        },
        '& .MuiInput-underline:before': {
            display: 'none',
            borderBottom: '0 transparent'
        },
        '& .MuiInput-underline:after': {
            borderBottom: '0 transparent'
        }
    },
    clearButton: {
        background: '#fff',
        borderRadius: 100,
        '@media (max-width: 460px)': {
            display: 'none'
        }
    },
    searchButton: {
        margin: 4,
        padding: 9
    },
    submitButton: {
        borderRadius: 18,
        padding: '15px 40px',
        fontSize: 16
    },
    settingsButton: {
        display: 'none',
        marginLeft: 34,
        borderRadius: 18,
        background: '#fff',
        boxShadow: '0px 10px 40px rgba(186, 184, 176, 0.2)',
        '@media (max-width: 1024px)': {
            display: 'block'
        }
    }
});

const SearchBar = ({ onSubmit, customClasses, hideSettings, showSearchButton, hintText, showSubmitButton }) => {
    const { classes, cx } = useStyles();
    const history = useHistory();
    const { searchStore, antiBotStore } = useStores();

    const inputRef = useShortcutSearchInput();

    const updateHistory = useCallback(
        (query = '', skip) => {
            let url = `/search?query=${query}`;
            if (skip) {
                url += `&skip=${skip}`;
            }

            history.push(url);
            Object.assign(history.location, { query, skip }); // Не понятно зачем это
        },
        [history]
    );

    const handleSubmit = useCallback(() => {
        searchStore.doRequest({ query: searchStore.query });
        updateHistory(searchStore.query);
        antiBotStore.randomCollectData();
        onSubmit && onSubmit(searchStore.query);
    }, [antiBotStore, onSubmit, searchStore, updateHistory]);

    const handleClear = useCallback(() => searchStore.setQuery(), [searchStore]);

    return (
        <div className={cx(classes.searchBarRoot, customClasses.root)}>
            <div className={cx(classes.searchBar, customClasses.searchBar)}>
                <div className={cx(classes.searchField, customClasses.field)}>
                    <SearchField
                        id="search-field"
                        inputRef={inputRef}
                        hintText={hintText || 'Search...'}
                        handleSubmit={handleSubmit}
                        classes={{ input: cx(classes.searchFieldInput, customClasses.input) }}
                    />
                    {searchStore.query && (
                        <Tooltip title="Clear">
                            <IconButton className={classes.clearButton} onClick={handleClear} size="small">
                                <SvgIcon component={IconClose} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {showSearchButton && (
                        <Tooltip title="Search">
                            <IconButton className={classes.searchButton} onClick={handleSubmit} size="small">
                                <SvgIcon component={IconZoom} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {showSubmitButton && (
                        <Button
                            variant="contained"
                            color="primary"
                            className={cx(classes.submitButton, customClasses.submitButton)}
                            onClick={handleSubmit}
                        >
                            Search
                        </Button>
                    )}
                </div>

                {!hideSettings && (
                    <Tooltip title="Search settings">
                        <IconButton
                            className={classes.settingsButton}
                            onClick={() => searchStore.openSettings()}
                            size="large"
                        >
                            <SvgIcon component={IconSettings} />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            {
                <Collapse in={searchStore.settingsOpen}>
                    <SearchBarSettings />
                </Collapse>
            }
        </div>
    );
};

SearchBar.propTypes = {
    onSubmit: PropTypes.func,
    customClasses: PropTypes.shape({
        root: PropTypes.string,
        searchBar: PropTypes.string,
        field: PropTypes.string,
        input: PropTypes.string,
        submitButton: PropTypes.string
    }),
    hideSettings: PropTypes.bool,
    showSearchButton: PropTypes.bool,
    showSubmitButton: PropTypes.bool,
    hintText: PropTypes.string
};

SearchBar.defaultProps = {
    customClasses: {}
};

export default observer(SearchBar);
